import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { Trash, Plus, X, PencilSimple, CheckCircle, CaretRight, CaretDown, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useColumnPrefs } from "@/lib/useColumnPrefs";
import { ColumnChooser } from "@/components/ColumnChooser";
import { money } from "@/lib/money";

const PR_COLUMNS = [
  { key: "week", label: "Week", locked: true },
  { key: "status", label: "Status" },
  { key: "pay_total", label: "Pay Total" },
  { key: "invoiced_total", label: "Invoiced Total" },
  { key: "profit", label: "Profit" },
];

const KIND_LABEL = {
  weekly_flat: "Flat weekly",
  salary: "Salary",
  per_billed_hour: "Commission",
  sales_comp: "Sales rep pay",
  distribution: "Distribution",
  distribution_extra: "Extra distribution",
};
const KIND_TONE = {
  weekly_flat: "bg-sky-100 text-sky-700",
  salary: "bg-indigo-100 text-indigo-700",
  per_billed_hour: "bg-teal-100 text-teal-700",
  sales_comp: "bg-emerald-100 text-emerald-800",
  distribution: "bg-violet-100 text-violet-700",
  distribution_extra: "bg-amber-100 text-amber-800",
};

function mondayOf(dateStr) {
  // Parse as LOCAL date (not UTC) to avoid timezone-shift moving a Monday into Sunday.
  let d;
  if (dateStr) {
    const parts = dateStr.split("-").map(Number);
    d = new Date(parts[0], parts[1] - 1, parts[2]);
  } else {
    d = new Date();
  }
  const wd = d.getDay();
  const diff = (wd === 0 ? -6 : 1) - wd;
  d.setDate(d.getDate() + diff);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const extraOf = (p) => (p.lines || []).filter((l) => l.kind === "distribution_extra")
  .reduce((s, l) => s + (l.gross_pay || 0), 0);

export default function PayRuns() {
  const [rows, setRows] = useState([]);
  const [week, setWeek] = useState(mondayOf());
  const [detail, setDetail] = useState(null);
  const [invBreakdown, setInvBreakdown] = useState(null); // {week, loading, data}
  const [missed, setMissed] = useState([]); // approved timesheets not in any pay run (safety net)
  const [unmatched, setUnmatched] = useState(null); // {count,total,invoices} — sent invoices counted in no pay week
  const [showUnmatched, setShowUnmatched] = useState(false);
  const [expanded, setExpanded] = useState({}); // week_start -> bool (multi-run weeks)
  const [weekEdit, setWeekEdit] = useState(null); // new week_start while editing the detail's week

  const openBreakdown = async (p) => {
    setInvBreakdown({ week: p.week_start, loading: true, data: null });
    try {
      const { data } = await api.get(`/payruns/${p.id}/invoiced-breakdown`);
      setInvBreakdown({ week: p.week_start, loading: false, data });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setInvBreakdown(null);
    }
  };
  // Week-level breakdown: merge every run's attributed invoices for that week.
  const openWeekBreakdown = async (g) => {
    setInvBreakdown({ week: g.ws, loading: true, data: null, runs: g.runs.length });
    try {
      const results = await Promise.all(g.runs.map((p) => api.get(`/payruns/${p.id}/invoiced-breakdown`)));
      const invoices = results.flatMap((r) => r.data?.invoices || []);
      const invoiced_total = results.reduce((s, r) => s + (r.data?.invoiced_total || 0), 0);
      setInvBreakdown({ week: g.ws, loading: false, data: { invoices, invoiced_total }, runs: g.runs.length });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setInvBreakdown(null);
    }
  };
  const [showAddModal, setShowAddModal] = useState(false);
  const [available, setAvailable] = useState([]);
  const [selectedTs, setSelectedTs] = useState([]);
  const [adding, setAdding] = useState(false);
  const [distModal, setDistModal] = useState(null); // { worker_id, amount, note }
  const [tsEdit, setTsEdit] = useState(null); // { idx, worker_name, total_hours, pay_rate, per_diem_days, per_diem_rate, exceptions_total, gross_pay }
  const [tsEditBusy, setTsEditBusy] = useState(false);
  const [workers, setWorkers] = useState([]);
  useEffect(() => { api.get("/workers").then((r) => setWorkers(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  // Editing a processed (approved/paid) run is allowed but must be deliberate.
  const guardProcessed = (what) => {
    if (!detail || detail.status === "draft") return true;
    const tail = detail.status === "paid"
      ? "\n\nThe ACH file for this run was already sent to the bank, so any pay difference is logged under \"Post-payment adjustments\" — pay it with the supplemental ACH file or mark it settled outside the bank."
      : "\n\nThe ACH file hasn't been generated yet, so the full NACHA download will include this change.";
    return window.confirm(`This pay run is already ${detail.status.toUpperCase()}.\n\n${what} will change its recorded totals.${tail}\n\nContinue?`);
  };

  const editLine = async (idx, line) => {
    if (!guardProcessed("Editing this line")) return;
    const v = window.prompt(`New amount for ${line.worker_name} (${KIND_LABEL[line.kind] || line.kind}):`, line.gross_pay);
    if (v === null) return;
    const amt = parseFloat(v);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount."); return; }
    try {
      const { data } = await api.patch(`/payruns/${detail.id}/lines/${idx}`, { gross_pay: amt });
      setDetail(data); load();
      toast.success(`${line.worker_name} updated to ${money(amt)}.`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const computeBase = (s) => Math.round(((parseFloat(s.total_hours) || 0) * (parseFloat(s.pay_rate) || 0)
    + (parseInt(s.per_diem_days) || 0) * (parseFloat(s.per_diem_rate) || 0)
    + (parseFloat(s.exceptions_total) || 0)) * 100) / 100;
  const openTsEdit = (idx, l) => {
    if (!guardProcessed("Correcting this pay line")) return;
    const orig = {
      total_hours: l.total_hours, pay_rate: l.pay_rate,
      per_diem_days: l.per_diem_days, per_diem_rate: l.per_diem_rate,
      exceptions_total: l.exceptions_total, gross_pay: l.gross_pay,
    };
    setTsEdit({ idx, worker_name: l.worker_name, ...orig, orig, grossTouched: false });
  };
  // Auto-update gross as fields change: apply the DELTA so OT/holiday premiums
  // baked into the original gross are preserved. Stops once gross is typed manually.
  const updateTsField = (key, val) => {
    setTsEdit((s) => {
      const next = { ...s, [key]: val };
      if (!s.grossTouched) {
        const delta = computeBase(next) - computeBase(s.orig);
        next.gross_pay = Math.round((Number(s.orig.gross_pay) + delta) * 100) / 100;
      }
      return next;
    });
  };
  const tsEditComputed = tsEdit ? computeBase(tsEdit) : 0;
  const saveTsEdit = async (e) => {
    e.preventDefault();
    setTsEditBusy(true);
    try {
      const { data } = await api.patch(`/payruns/${detail.id}/lines/${tsEdit.idx}`, {
        total_hours: parseFloat(tsEdit.total_hours) || 0,
        pay_rate: parseFloat(tsEdit.pay_rate) || 0,
        per_diem_days: parseInt(tsEdit.per_diem_days) || 0,
        per_diem_rate: parseFloat(tsEdit.per_diem_rate) || 0,
        exceptions_total: parseFloat(tsEdit.exceptions_total) || 0,
        gross_pay: parseFloat(tsEdit.gross_pay) || 0,
      });
      setDetail(data); setTsEdit(null); load();
      toast.success(`${tsEdit.worker_name}'s pay line updated.`);
    } catch (e2) { toast.error(e2.response?.data?.detail || e2.message); }
    finally { setTsEditBusy(false); }
  };

  const removeLine = async (idx, line) => {
    if (!guardProcessed("Removing this line")) return;
    const isTs = (line.kind || "timesheet") === "timesheet";
    const msg = isTs
      ? `Remove ${line.worker_name}'s timesheet line (${money(line.gross_pay)}) from this pay run?\n\nThe ${(line.timesheet_ids || []).length} timesheet(s) behind it are released so they can be paid in another pay run.`
      : `Remove ${line.worker_name}'s ${KIND_LABEL[line.kind] || line.kind} line (${money(line.gross_pay)}) from this pay run?`;
    if (!confirm(msg)) return;
    try {
      const { data } = await api.delete(`/payruns/${detail.id}/lines/${idx}`);
      setDetail(data); load();
      toast.success("Line removed.");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const addDistribution = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/payruns/${detail.id}/lines`, {
        worker_id: distModal.worker_id, amount: parseFloat(distModal.amount),
        note: distModal.note, kind: distModal.kind,
      });
      setDetail(data); setDistModal(null); load();
      toast.success(distModal.kind === "distribution_extra"
        ? "Additional distribution added (excluded from profit reports)."
        : `${KIND_LABEL[distModal.kind]} line added — counted in the pay run total.`);
    } catch (e2) { toast.error(e2.response?.data?.detail || e2.message); }
  };

  const load = () => {
    api.get("/payruns").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
    api.get("/payruns/missed-timesheets").then((r) => setMissed(Array.isArray(r.data?.items) ? r.data.items : [])).catch(() => {});
    api.get("/payruns/unmatched-invoices").then((r) => setUnmatched(r.data || null)).catch(() => {});
  };
  useEffect(() => { load(); }, []);


  const assemble = async () => {
    const existing = rows.filter((r) => r.week_start === week);
    if (existing.length > 0) {
      const ok = window.confirm(
        `A pay run for the week of ${week} already exists (${existing.map((r) => r.status).join(", ")}).\n\n`
        + "Creating another one for the same week is allowed (the list will show a combined week total), "
        + "but if you only need to add a missed timesheet, it's cleaner to open the existing run and use \"Add Timesheets\".\n\n"
        + "Create a second pay run anyway?");
      if (!ok) return;
    }
    try {
      const { data } = await api.post("/payruns/assemble", { week_start: week });
      setDetail(data);
      load();
      toast.success("Pay run assembled successfully");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const refreshDetail = async (id) => {
    try { const { data } = await api.get(`/payruns/${id || detail?.id}`); setDetail(data); } catch { /* ignore */ }
    load();
  };

  const saveWeek = async () => {
    if (!weekEdit || weekEdit === detail.week_start) { setWeekEdit(null); return; }
    try {
      const { data } = await api.patch(`/payruns/${detail.id}`, { week_start: weekEdit });
      setDetail(data); setWeekEdit(null); load();
      toast.success(`Pay run moved to week of ${data.week_start}.`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const settleSupps = async () => {
    if (!window.confirm("Mark all unpaid adjustments on this pay run as settled outside the bank (check, Zelle, wire…)?")) return;
    try {
      const { data } = await api.post(`/payruns/${detail.id}/supplements/mark-paid`);
      toast.success(`${data.settled} adjustment(s) marked settled.`);
      refreshDetail();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const approve = async (id) => {
    try {
      await api.post(`/payruns/${id}/approve`);
      load();
      const { data } = await api.get(`/payruns/${id}`);
      setDetail(data);
      toast.success("Pay run approved");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const markPaid = async (id) => {
    if (!window.confirm("Mark this pay run as PAID without generating an ACH file?\n\nUse this when the workers were paid outside the bank (paper check, Zelle, wire, etc.). This cannot be undone.")) return;
    try {
      await api.post(`/payruns/${id}/mark-paid`);
      load();
      const { data } = await api.get(`/payruns/${id}`);
      setDetail(data);
      toast.success("Pay run marked paid (no ACH file)");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const deletePayrun = async (id, weekStart) => {
    const pr = rows.find((r) => r.id === id) || (detail?.id === id ? detail : null);
    const status = pr?.status || "draft";
    const hasComp = (pr?.lines || []).some((l) => ["salary", "distribution", "weekly_flat", "per_billed_hour", "sales_comp"].includes(l.kind));
    const compWarn = hasComp
      ? "\n\n⚠️ This pay run contains weekly salary/distribution lines. If another pay run already exists for this week, those lines will NOT be re-added automatically — you'd need to re-add them via Add Distribution."
      : "";
    const processedWarn = status !== "draft"
      ? `\n\n⚠️ This run is ${status.toUpperCase()} (total ${money(pr?.total || 0)}). Deleting it removes it from all totals and reports${status === "paid" ? " — it does NOT reverse any ACH payment already sent to the bank" : ""}.`
      : "";
    if (!confirm(`Delete this ${status} pay run (week of ${weekStart})?\n\nAll timesheets will be released so they can be re-assembled into a new pay run or added to another one.${processedWarn}${compWarn}`)) return;
    try {
      const { data } = await api.delete(`/payruns/${id}`);
      toast.success(`Pay run deleted. ${data.released} timesheet(s) released.`);
      if (detail?.id === id) setDetail(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const { vis, hiddenSet, toggleColumn, resetColumns } = useColumnPrefs("payruns", PR_COLUMNS);
  const prVisCount = PR_COLUMNS.length - hiddenSet.size + 1;

  // One entry per week; weeks with several runs get combined totals + an expander.
  const groups = useMemo(() => {
    const m = new Map();
    for (const p of rows) { if (!m.has(p.week_start)) m.set(p.week_start, []); m.get(p.week_start).push(p); }
    return [...m.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1)).map(([ws, runs]) => {
      const total = runs.reduce((s, p) => s + (p.total || 0), 0);
      const extra = runs.reduce((s, p) => s + extraOf(p), 0);
      const invoiced = runs.reduce((s, p) => s + (p.invoiced_total || 0), 0);
      return { ws, runs, total, invoiced, profit: invoiced - (total - extra) };
    });
  }, [rows]);

  const openAddModal = async (preselect = []) => {
    if (!guardProcessed("Adding timesheets")) return;
    try {
      const { data } = await api.get("/payruns/available-timesheets");
      const list = Array.isArray(data) ? data : [];
      setAvailable(list);
      const ids = new Set(list.map((t) => t.id));
      setSelectedTs(preselect.filter((id) => ids.has(id)));
      setShowAddModal(true);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const toggleTs = (id) => {
    setSelectedTs((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const addSelected = async () => {
    if (selectedTs.length === 0) return;
    setAdding(true);
    try {
      const { data } = await api.post(`/payruns/${detail.id}/add-timesheets`, { timesheet_ids: selectedTs });
      setDetail(data);
      setShowAddModal(false);
      toast.success(`Added ${selectedTs.length} timesheet(s) to pay run`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setAdding(false);
  };

  const profitTone = (v) => (v > 0 ? "text-green-600" : v < 0 ? "text-red-600" : "text-slate-400");
  const renderRun = (p, child = false) => {
    const profit = (p.invoiced_total || 0) - (p.total - extraOf(p));
    return (
      <tr key={p.id} className={`border-t border-slate-100 hover:bg-slate-50 ${child ? "bg-slate-50/60 text-slate-600" : ""}`}
        data-testid={`payrun-row-${p.id}`}>
        <td className={`px-4 py-3 ${child ? "pl-10 text-xs font-mono" : ""}`}>
          {child ? <span className="text-slate-400">↳ run {p.id.slice(0, 8)}{p.created_at ? ` · ${new Date(p.created_at).toLocaleDateString()}` : ""}</span> : p.week_start}
        </td>
        {vis("status") && <td className="px-4 py-3"><StatusPill status={p.status} /></td>}
        {vis("pay_total") && <td className="px-4 py-3 text-right font-mono">{money(p.total)}</td>}
        {vis("invoiced_total") && <td className="px-4 py-3 text-right font-mono">
          <button onClick={() => openBreakdown(p)} data-testid={`invoiced-total-btn-${p.id}`}
            title="See which invoices make up this total and why"
            className="underline decoration-dotted underline-offset-4 hover:text-green-700">
            {money(p.invoiced_total || 0)}
          </button>
        </td>}
        {vis("profit") && <td className={`px-4 py-3 text-right font-mono font-semibold ${profitTone(profit)}`}
          data-testid={`payrun-profit-${p.id}`}>
          {profit > 0 ? "+" : ""}{money(profit)}
        </td>}
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => refreshDetail(p.id)} data-testid={`open-payrun-${p.id}`}
              className="text-green-600 font-semibold hover:underline">Open</button>
            <button data-testid={`delete-payrun-${p.id}`} onClick={() => deletePayrun(p.id, p.week_start)}
              className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
              title={p.status === "draft" ? "Delete draft pay run" : `Delete this ${p.status} pay run`}>
              <Trash size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const unpaidSupps = (detail?.supplements || []).filter((s) => !s.paid_at);
  const unpaidNet = unpaidSupps.reduce((s, x) => s + (x.amount || 0), 0);
  const unpaidPositive = Object.values(unpaidSupps.reduce((acc, x) => { acc[x.worker_id] = (acc[x.worker_id] || 0) + (x.amount || 0); return acc; }, {}))
    .filter((v) => v > 0).reduce((s, v) => s + v, 0);

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <div className="tinylabel">Payroll</div>
        <h1 className="text-3xl font-black">Pay Runs</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Week starting (Mon)</label>
          <input type="date" value={week} onChange={(e) => setWeek(mondayOf(e.target.value))}
            data-testid="payrun-week-input" className="mt-1 h-11 px-3 border border-slate-300 rounded-md" />
        </div>
        <button data-testid="assemble-payrun-btn" onClick={assemble}
          className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md">
          Assemble pay run
        </button>
        <div className="ml-auto">
          <ColumnChooser columns={PR_COLUMNS} hiddenSet={hiddenSet} onToggle={toggleColumn} onReset={resetColumns} />
        </div>
      </div>

      {missed.length > 0 && (
        <div data-testid="missed-ts-banner" className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-4">
          <div className="flex items-start gap-3">
            <Warning size={22} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-amber-900">
                <span data-testid="missed-ts-count">{missed.length}</span> approved timesheet{missed.length === 1 ? " is" : "s are"} not in any pay run
              </div>
              <p className="text-sm text-amber-800 mt-0.5">
                Payroll has already been assembled for {missed.length === 1 ? "this week" : "these weeks"} (or a later one), so
                {missed.length === 1 ? " this worker hasn't" : " these workers haven't"} been paid for {missed.length === 1 ? "it" : "them"}.
                {detail ? " Add them to the open pay run below, or open the run for the right week first." : " Open the pay run you want to add them to, then click \"Add these to this run\"."}
              </p>
              <ul className="mt-2 text-sm divide-y divide-amber-200/70">
                {missed.map((t) => (
                  <li key={t.id} data-testid={`missed-ts-${t.id}`} className="py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-semibold text-slate-800">{t._worker_name}</span>
                    <span className="text-slate-600">{t._project_name}</span>
                    <span className="font-mono text-xs text-slate-600">w/o {t.week_start}</span>
                    <span className="font-mono text-xs text-slate-600">{t.total_hours}h{t.per_diem_days ? ` · ${t.per_diem_days} PD` : ""}</span>
                    <StatusPill status={t.status} />
                  </li>
                ))}
              </ul>
            </div>
            {detail && (
              <button data-testid="missed-ts-add-btn" onClick={() => openAddModal(missed.map((t) => t.id))}
                className="h-10 px-4 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-md shrink-0 transition-colors">
                Add these to week of {detail.week_start}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto mb-6">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Week</th>
              {vis("status") && <th className="text-left px-4 py-3">Status</th>}
              {vis("pay_total") && <th className="text-right px-4 py-3">Pay Total</th>}
              {vis("invoiced_total") && <th className="text-right px-4 py-3">Invoiced Total</th>}
              {vis("profit") && <th className="text-right px-4 py-3">Profit</th>}
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={prVisCount} className="px-4 py-6 text-slate-400">No pay runs yet.</td></tr>}
            {groups.map((g) => {
              if (g.runs.length === 1) return renderRun(g.runs[0]);
              const open = !!expanded[g.ws];
              return (
                <React.Fragment key={g.ws}>
                  <tr className="border-t border-slate-200 bg-violet-50/40 hover:bg-violet-50" data-testid={`week-group-row-${g.ws}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => setExpanded((e) => ({ ...e, [g.ws]: !open }))} data-testid={`week-group-toggle-${g.ws}`}
                        className="flex items-center gap-1.5 font-semibold text-slate-800 hover:text-violet-700"
                        title={open ? "Hide individual pay runs" : "Show individual pay runs"}>
                        {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                        {g.ws}
                        <span className="ml-1 text-[10px] font-black uppercase tracking-wider bg-violet-100 text-violet-700 rounded-full px-2 py-0.5">
                          {g.runs.length} runs
                        </span>
                      </button>
                    </td>
                    {vis("status") && <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">{g.runs.map((p) => <StatusPill key={p.id} status={p.status} />)}</div>
                    </td>}
                    {vis("pay_total") && <td className="px-4 py-3 text-right font-mono font-semibold" data-testid={`week-group-total-${g.ws}`}>{money(g.total)}</td>}
                    {vis("invoiced_total") && <td className="px-4 py-3 text-right font-mono font-semibold">
                      <button onClick={() => openWeekBreakdown(g)} data-testid={`week-group-invoiced-${g.ws}`}
                        title="Combined invoiced total for every pay run in this week"
                        className="underline decoration-dotted underline-offset-4 hover:text-green-700">
                        {money(g.invoiced)}
                      </button>
                    </td>}
                    {vis("profit") && <td className={`px-4 py-3 text-right font-mono font-bold ${profitTone(g.profit)}`} data-testid={`week-group-profit-${g.ws}`}>
                      {g.profit > 0 ? "+" : ""}{money(g.profit)}
                    </td>}
                    <td className="px-4 py-3 text-right text-xs text-slate-400">week total</td>
                  </tr>
                  {open && g.runs.map((p) => renderRun(p, true))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {unmatched && unmatched.count > 0 && (
        <div data-testid="unmatched-invoices-note" className="-mt-4 mb-6 text-xs text-slate-500 flex flex-wrap items-center gap-1">
          <Warning size={14} weight="fill" className="text-slate-400" />
          <span><b data-testid="unmatched-invoices-count">{unmatched.count}</b> sent invoice{unmatched.count === 1 ? "" : "s"} totaling <b>{money(unmatched.total)}</b> {unmatched.count === 1 ? "isn't" : "aren't"} counted in any pay week yet.</span>
          <button data-testid="unmatched-invoices-btn" onClick={() => setShowUnmatched(true)}
            className="underline decoration-dotted underline-offset-4 hover:text-slate-800 font-semibold">See why</button>
        </div>
      )}

      {showUnmatched && unmatched && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" onClick={() => setShowUnmatched(false)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="unmatched-invoices-modal"
            className="bg-white rounded-lg w-full max-w-2xl p-6 border border-slate-200 shadow-xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-1">
              <div>
                <div className="tinylabel">Not counted in any pay week</div>
                <h2 className="text-xl font-bold">{unmatched.count} invoice{unmatched.count === 1 ? "" : "s"} · {money(unmatched.total)}</h2>
              </div>
              <button onClick={() => setShowUnmatched(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Most of these are simply waiting: their timesheets haven't been paid in a pay run yet, so they'll
              appear on that week automatically once you assemble it. Manual invoices with no timesheets only count
              when a pay run exists for their week ending or was assembled the same week they were created.
            </p>
            <div className="divide-y divide-slate-100">
              {unmatched.invoices.map((inv) => (
                <div key={inv.invoice_id} className="py-3" data-testid={`unmatched-invoice-${inv.invoice_id}`}>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Link to={`/invoices/${inv.invoice_id}`} className="font-bold underline decoration-dotted hover:text-green-700">
                      {inv.number || "(no number)"}
                    </Link>
                    {inv.client_name && <span className="text-slate-500">{inv.client_name}</span>}
                    {inv.week_ending && <span className="text-xs text-slate-400">wk end {inv.week_ending}</span>}
                    <StatusPill status={inv.status} />
                    <span className="ml-auto font-mono font-bold">{money(inv.total)}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {inv.reason_label}{inv.waiting_count ? ` (${inv.waiting_count} unpaid timesheet${inv.waiting_count === 1 ? "" : "s"})` : ""}
                    {inv.invoice_date && <> — generated {inv.invoice_date}</>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {invBreakdown && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50" onClick={() => setInvBreakdown(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="invoiced-breakdown-modal"
            className="bg-white rounded-lg w-full max-w-2xl p-6 border border-slate-200 shadow-xl max-h-[85vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-1">
              <div>
                <div className="tinylabel">Invoiced total breakdown</div>
                <h2 className="text-xl font-bold">Week of {invBreakdown.week}{invBreakdown.runs > 1 ? ` · ${invBreakdown.runs} pay runs combined` : ""}</h2>
              </div>
              <button onClick={() => setInvBreakdown(null)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Rule: an invoice counts once, on the pay run that paid its timesheets — no matter which day either
              was generated. Stragglers spanning two runs count whole on the most recently assembled run.
              Billing-only and historic invoices match the work week of their week ending.
            </p>
            {invBreakdown.loading ? (
              <div className="text-slate-400 text-sm py-6">Loading…</div>
            ) : (
              <>
                {(invBreakdown.data?.invoices || []).length === 0 && (
                  <div className="text-slate-400 text-sm py-4">No invoices are attributed to this week.</div>
                )}
                <div className="divide-y divide-slate-100">
                  {(invBreakdown.data?.invoices || []).map((inv) => (
                    <div key={inv.invoice_id} className="py-3" data-testid={`breakdown-invoice-${inv.invoice_id}`}>
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <Link to={`/invoices/${inv.invoice_id}`} className="font-bold underline decoration-dotted hover:text-green-700">
                          {inv.number || "(no number)"}
                        </Link>
                        {inv.client_name && <span className="text-slate-500">{inv.client_name}</span>}
                        {inv.week_ending && <span className="text-xs text-slate-400">wk end {inv.week_ending}</span>}
                        <StatusPill status={inv.status} />
                        <span className="ml-auto font-mono font-bold">{money(inv.total)}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {inv.reason_label}
                        {inv.invoice_date && <> — generated {inv.invoice_date}</>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center border-t border-slate-200 mt-2 pt-3 text-sm">
                  <span className="tinylabel">Total</span>
                  <span className="font-mono font-black" data-testid="breakdown-total">{money(invBreakdown.data?.invoiced_total || 0)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {detail && (
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="tinylabel">Pay run detail</div>
              {weekEdit === null ? (
                <h2 className="text-xl font-bold flex items-center gap-2 flex-wrap">
                  <span>Week of {detail.week_start} · Total {money(detail.total)}</span>
                  <button data-testid="edit-week-btn" onClick={() => setWeekEdit(detail.week_start)}
                    title="Change this pay run's week" className="p-1 text-slate-400 hover:text-blue-600">
                    <PencilSimple size={16} weight="bold" />
                  </button>
                </h2>
              ) : (
                <div className="flex items-center gap-2 mt-1 flex-wrap" data-testid="edit-week-form">
                  <input type="date" value={weekEdit} onChange={(e) => setWeekEdit(mondayOf(e.target.value))}
                    data-testid="edit-week-input" className="h-10 px-3 border border-slate-300 rounded-md" />
                  <button data-testid="edit-week-save" onClick={saveWeek}
                    className="h-10 px-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md">Save week</button>
                  <button onClick={() => setWeekEdit(null)} className="h-10 px-3 border border-slate-300 rounded-md font-semibold">Cancel</button>
                  <span className="text-xs text-slate-500">Snaps to Monday · totals re-anchor to the new week</span>
                </div>
              )}
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <StatusPill status={detail.status} />
                {detail.status !== "draft" && (
                  <span data-testid="processed-edit-note" className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Processed run — edits are allowed and audit-logged{detail.status === "paid" ? "; pay differences are tracked under Post-payment adjustments" : ""}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button data-testid="add-timesheets-btn" onClick={() => openAddModal()}
                className="h-11 px-4 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold rounded-md flex items-center gap-2 transition-colors">
                <Plus size={16} /> Add Timesheets
              </button>
              <button data-testid="add-distribution-btn"
                onClick={() => { if (guardProcessed("Adding a pay line")) setDistModal({ worker_id: "", amount: "", note: "", kind: "distribution_extra" }); }}
                className="h-11 px-4 bg-violet-50 hover:bg-violet-100 text-violet-700 font-semibold rounded-md flex items-center gap-2 transition-colors">
                <Plus size={16} /> Add Distribution
              </button>
              {detail.status === "draft" && (
                <button data-testid="approve-payrun-btn" onClick={() => approve(detail.id)}
                  className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md transition-colors">
                  Approve
                </button>
              )}
              {(detail.status === "approved" || detail.status === "paid") && (
                <a data-testid="download-nacha-btn" href={`${API_BASE}/payruns/${detail.id}/nacha`} target="_blank" rel="noreferrer"
                  onClick={() => setTimeout(() => refreshDetail(detail.id), 1500)}
                  className="h-11 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md flex items-center transition-colors">
                  Download NACHA file
                </a>
              )}
              {detail.status === "approved" && (
                <button data-testid="mark-paid-btn" onClick={() => markPaid(detail.id)}
                  title="Mark paid without generating an ACH file (paid outside the bank)"
                  className="h-11 px-4 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold rounded-md flex items-center gap-2 transition-colors">
                  <CheckCircle size={16} weight="bold" /> Mark paid (no ACH)
                </button>
              )}
              {detail.status === "paid" && detail.paid_via === "manual" && (
                <span data-testid="paid-manual-note" className="self-center text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1">
                  Paid outside the bank{detail.paid_at ? ` · ${new Date(detail.paid_at).toLocaleDateString()}` : ""}
                </span>
              )}
              <button data-testid={`delete-payrun-detail-${detail.id}`} onClick={() => deletePayrun(detail.id, detail.week_start)}
                className="h-11 px-4 bg-red-50 hover:bg-red-100 text-red-600 font-semibold rounded-md flex items-center gap-2 transition-colors">
                <Trash size={16} /> {detail.status === "draft" ? "Delete Draft" : "Delete Pay Run"}
              </button>
            </div>
          </div>

          {missed.length > 0 && (
            <div data-testid="detail-missed-offer" className="mb-4 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-md px-4 py-3 text-sm text-amber-900">
              <Warning size={18} weight="fill" className="text-amber-600" />
              <span><b>{missed.length}</b> approved timesheet{missed.length === 1 ? "" : "s"} from other weeks {missed.length === 1 ? "isn't" : "aren't"} in any pay run.</span>
              <button data-testid="detail-missed-add-btn" onClick={() => openAddModal(missed.map((t) => t.id))}
                className="ml-auto h-9 px-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-md transition-colors">
                Add these {missed.length} to this run
              </button>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Worker</th>
                  <th className="text-right px-4 py-3">Hours</th>
                  <th className="text-right px-4 py-3">Pay Rate</th>
                  <th className="text-right px-4 py-3">Per Diem</th>
                  <th className="text-right px-4 py-3">Exceptions</th>
                  <th className="text-right px-4 py-3">Gross Pay</th>
                  <th className="px-4 py-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {detail.lines.map((l, i) => {
                  const special = (l.kind || "timesheet") !== "timesheet";
                  const showBreakdown = !special && Array.isArray(l.breakdown)
                    && (l.breakdown.length > 1 || l.mixed_rates
                        || l.breakdown.some((b) => (b.ot_hours || 0) > 0 || (b.holiday_hours || 0) > 0));
                  return (
                  <React.Fragment key={i}>
                  <tr className="border-t border-slate-100" data-testid={`payrun-line-${i}`}>
                    <td className="px-4 py-3 font-medium">
                      {l.worker_name}
                      {special && (
                        <span className={`ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider ${KIND_TONE[l.kind] || "bg-slate-100 text-slate-600"}`}>
                          {KIND_LABEL[l.kind] || l.kind}
                        </span>
                      )}
                      {l.edited && (
                        <span data-testid={`edited-badge-${i}`} title={`Manually corrected by ${l.edited_by || "admin"}`}
                          className="ml-2 inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-100 text-amber-800">
                          Edited
                        </span>
                      )}
                      {l.note && <div className="text-xs text-slate-400 font-normal">{l.note}</div>}
                    </td>
                    <td className="text-right px-4 py-3 font-mono">{special && !["per_billed_hour", "sales_comp"].includes(l.kind) ? "—" : l.total_hours}</td>
                    <td className="text-right px-4 py-3 font-mono">{special && !["per_billed_hour", "sales_comp"].includes(l.kind) ? "—" : l.mixed_rates ? <span className="text-amber-700 font-semibold" title="Timesheets in this line pay at different rates — see the breakdown below">mixed</span> : money(l.pay_rate)}</td>
                    <td className="text-right px-4 py-3 font-mono">{special ? "—" : l.mixed_rates ? `${l.per_diem_days} days` : `${l.per_diem_days} × ${money(l.per_diem_rate)}`}</td>
                    <td className="text-right px-4 py-3 font-mono">{special ? "—" : money(l.exceptions_total)}</td>
                    <td className="text-right px-4 py-3 font-mono font-bold">{money(l.gross_pay)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {special && (
                        <>
                          <button onClick={() => editLine(i, l)} data-testid={`edit-line-${i}`}
                            title="Edit amount" className="p-1 text-slate-400 hover:text-blue-600">
                            <PencilSimple size={15} weight="bold" />
                          </button>
                          <button onClick={() => removeLine(i, l)} data-testid={`remove-line-${i}`}
                            title="Remove from this pay run" className="p-1 text-slate-400 hover:text-red-600">
                            <Trash size={15} />
                          </button>
                        </>
                      )}
                      {!special && (
                        <>
                          <button onClick={() => openTsEdit(i, l)} data-testid={`edit-ts-line-${i}`}
                            title="Correct this worker's pay line" className="p-1 text-slate-400 hover:text-blue-600">
                            <PencilSimple size={15} weight="bold" />
                          </button>
                          <button onClick={() => removeLine(i, l)} data-testid={`remove-ts-line-${i}`}
                            title="Remove this line and release its timesheets" className="p-1 text-slate-400 hover:text-red-600">
                            <Trash size={15} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {showBreakdown && l.breakdown.map((b, bi) => (
                    <tr key={`${i}-${bi}`} className="bg-slate-50/60" data-testid={`payrun-line-${i}-breakdown-${bi}`}>
                      <td colSpan={7} className="px-4 py-1.5 text-xs text-slate-500 font-mono">
                        <span className="text-slate-300 mr-1">↳</span>
                        w/o {b.week_start} · <span className="text-slate-600">{b.project_name}</span>:{" "}
                        {b.reg_hours}h × {money(b.pay_rate)}
                        {(b.ot_hours || 0) > 0 && <> + OT {b.ot_hours}h × {money(b.ot_rate)}</>}
                        {(b.holiday_hours || 0) > 0 && <> + Hol {b.holiday_hours}h × {money(b.holiday_rate)}</>}
                        {(b.per_diem_days || 0) > 0 && <> + {b.per_diem_days} PD × {money(b.per_diem_rate)}</>}
                        {(b.exceptions || 0) > 0 && <> + exc {money(b.exceptions)}</>}
                        {" = "}<span className="font-bold text-slate-700">{money(b.gross)}</span>
                      </td>
                    </tr>
                  ))}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {(detail.supplements || []).length > 0 && (
            <div data-testid="supplements-card" className="mt-5 border border-amber-200 bg-amber-50/40 rounded-md p-4">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                <div>
                  <div className="tinylabel text-amber-700">Post-payment adjustments</div>
                  <div className="text-sm text-slate-700 mt-0.5">
                    Changes made after this run was paid. Unpaid net:{" "}
                    <b className="font-mono" data-testid="supp-unpaid-total">{unpaidNet >= 0 ? "" : "−"}{money(Math.abs(unpaidNet))}</b>
                    {unpaidSupps.length === 0 && <span className="ml-2 text-emerald-700 font-semibold">All settled</span>}
                  </div>
                </div>
                {unpaidSupps.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {unpaidPositive > 0 && (
                      <a data-testid="download-supp-nacha-btn" href={`${API_BASE}/payruns/${detail.id}/nacha-supplemental`} target="_blank" rel="noreferrer"
                        onClick={() => setTimeout(() => refreshDetail(detail.id), 1500)}
                        title="ACH file containing ONLY the unpaid adjustments (net per worker)"
                        className="h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-md flex items-center transition-colors">
                        Download supplemental NACHA · {money(unpaidPositive)}
                      </a>
                    )}
                    <button data-testid="supp-mark-paid-btn" onClick={settleSupps}
                      className="h-10 px-4 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-semibold rounded-md flex items-center gap-2 transition-colors">
                      <CheckCircle size={16} weight="bold" /> Mark settled outside the bank
                    </button>
                  </div>
                )}
              </div>
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-slate-500">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Date</th>
                    <th className="text-left py-1.5 pr-3">Worker</th>
                    <th className="text-left py-1.5 pr-3">Reason</th>
                    <th className="text-right py-1.5 pr-3">Amount</th>
                    <th className="text-left py-1.5">Settled</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.supplements.map((s) => (
                    <tr key={s.id} className="border-t border-amber-200/60" data-testid={`supplement-row-${s.id}`}>
                      <td className="py-1.5 pr-3 font-mono text-xs text-slate-600">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</td>
                      <td className="py-1.5 pr-3 font-medium">{s.worker_name}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{s.reason}</td>
                      <td className={`py-1.5 pr-3 text-right font-mono font-semibold ${s.amount < 0 ? "text-red-600" : "text-slate-800"}`}>
                        {s.amount < 0 ? "−" : "+"}{money(Math.abs(s.amount))}
                      </td>
                      <td className="py-1.5 text-xs">
                        {s.paid_at
                          ? <span className="font-semibold text-emerald-700">{s.paid_via === "ach" ? "Supplemental ACH" : "Outside the bank"} · {new Date(s.paid_at).toLocaleDateString()}</span>
                          : <span className="font-semibold text-amber-700">Unpaid</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-xs text-slate-500">
                Do <b>not</b> re-upload the main NACHA file to pay these — it would pay every worker again.
                Negative adjustments can't be pulled back by ACH; recover them outside the bank and mark them settled.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Edit timesheet pay line modal */}
      {tsEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setTsEdit(null)}>
          <form onSubmit={saveTsEdit} onClick={(e) => e.stopPropagation()}
                data-testid="edit-ts-line-modal"
                className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Correct {tsEdit.worker_name}'s pay line</h3>
              <button type="button" onClick={() => setTsEdit(null)} className="p-2 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="bg-sky-50 border border-sky-200 rounded-md px-3 py-2 mb-4 text-xs text-sky-800">
              One-time correction for THIS pay run only — the worker's timesheet is not changed.
              The line will be tagged <b>Edited</b> and the change is audit-logged.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Hours</label>
                <input type="number" step="0.01" min="0" required value={tsEdit.total_hours}
                  onChange={(e) => updateTsField("total_hours", e.target.value)}
                  data-testid="ts-edit-hours" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" /></div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Pay rate ($/hr)</label>
                <input type="number" step="0.01" min="0" required value={tsEdit.pay_rate}
                  onChange={(e) => updateTsField("pay_rate", e.target.value)}
                  data-testid="ts-edit-rate" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" /></div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Per-diem days</label>
                <input type="number" step="1" min="0" required value={tsEdit.per_diem_days}
                  onChange={(e) => updateTsField("per_diem_days", e.target.value)}
                  data-testid="ts-edit-pd-days" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" /></div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Per-diem rate ($)</label>
                <input type="number" step="0.01" min="0" required value={tsEdit.per_diem_rate}
                  onChange={(e) => updateTsField("per_diem_rate", e.target.value)}
                  data-testid="ts-edit-pd-rate" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" /></div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Exceptions ($)</label>
                <input type="number" step="0.01" min="0" required value={tsEdit.exceptions_total}
                  onChange={(e) => updateTsField("exceptions_total", e.target.value)}
                  data-testid="ts-edit-exceptions" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" /></div>
              <div><label className="text-xs font-bold uppercase tracking-widest text-slate-500">Gross pay ($)</label>
                <input type="number" step="0.01" min="0" required value={tsEdit.gross_pay}
                  onChange={(e) => setTsEdit({ ...tsEdit, gross_pay: e.target.value, grossTouched: true })}
                  data-testid="ts-edit-gross" className="mt-1 w-full h-11 px-3 border-2 border-green-500 rounded-md font-mono font-bold" />
                <div className="mt-1 text-[11px] text-slate-400">{tsEdit.grossTouched ? "Manual override — no longer auto-updating" : "Auto-updates as you change the fields"}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
              <span className="text-slate-600">
                Computed from fields: <b className="font-mono">{money(tsEditComputed)}</b>
                <span className="text-slate-400"> (regular rates only — excludes OT/holiday premiums)</span>
              </span>
              {Number(tsEdit.gross_pay) !== tsEditComputed && (
                <button type="button" data-testid="ts-edit-apply-computed"
                  onClick={() => setTsEdit({ ...tsEdit, gross_pay: tsEditComputed, grossTouched: true })}
                  className="ml-3 shrink-0 text-green-700 font-bold hover:underline">Use this</button>
              )}
            </div>
            <div className="mt-5 flex gap-3 justify-end">
              <button type="button" onClick={() => setTsEdit(null)}
                className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="ts-edit-save" disabled={tsEditBusy}
                className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md disabled:opacity-60">
                {tsEditBusy ? "Saving…" : "Save correction"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add extra distribution modal */}
      {distModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDistModal(null)}>
          <form onSubmit={addDistribution} onClick={(e) => e.stopPropagation()}
                data-testid="add-distribution-modal"
                className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Add pay line to this pay run</h3>
              <button type="button" onClick={() => setDistModal(null)} className="p-2 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Type</label>
            <select value={distModal.kind}
              onChange={(e) => setDistModal({ ...distModal, kind: e.target.value })}
              data-testid="dist-kind-select"
              className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white mb-3">
              <option value="distribution_extra">Extra distribution (one-off)</option>
              <option value="salary">Salary</option>
              <option value="distribution">Distribution</option>
            </select>
            {distModal.kind === "distribution_extra" ? (
              <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4 text-xs text-amber-800" data-testid="dist-kind-note">
                One-off <b>additional</b> distribution for this week only. It's paid in the ACH file but
                <b> excluded from gross-profit reports</b> (unlike the regular weekly target).
              </div>
            ) : (
              <div className="bg-violet-50 border border-violet-200 rounded-md px-3 py-2 mb-4 text-xs text-violet-800" data-testid="dist-kind-note">
                Adds a <b>{KIND_LABEL[distModal.kind]}</b> line to this pay run — labeled like the automatic weekly
                line, <b>counted in the pay run total and profit reports</b>, and paid in the ACH file.
                Use this when the weekly {KIND_LABEL[distModal.kind].toLowerCase()} is missing from this run.
              </div>
            )}
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Person</label>
            <select required value={distModal.worker_id}
              onChange={(e) => setDistModal({ ...distModal, worker_id: e.target.value })}
              data-testid="dist-worker-select"
              className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white mb-3">
              <option value="">Select…</option>
              {workers.filter((w) => w.status === "active").map((w) => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Amount ($)</label>
            <input required type="number" step="0.01" min="0.01" value={distModal.amount}
              onChange={(e) => setDistModal({ ...distModal, amount: e.target.value })}
              data-testid="dist-amount-input"
              className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono mb-3" />
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Note (optional)</label>
            <input value={distModal.note}
              onChange={(e) => setDistModal({ ...distModal, note: e.target.value })}
              data-testid="dist-note-input"
              placeholder="e.g. Q2 extra distribution"
              className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setDistModal(null)}
                className="h-10 px-4 border border-slate-300 text-slate-700 font-semibold rounded-md hover:bg-slate-50">Cancel</button>
              <button type="submit" data-testid="dist-save-btn"
                className="h-10 px-5 bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-md">Add to pay run</button>
            </div>
          </form>
        </div>
      )}

      {/* Add Timesheets Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div>
                <h3 className="text-lg font-bold">Add Timesheets to Pay Run</h3>
                <p className="text-sm text-slate-500 mt-0.5">Select any approved timesheets to include — regardless of week date</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-md">
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {available.length === 0 ? (
                <div className="p-10 text-center">
                  <div className="text-slate-400 font-medium">No available approved timesheets</div>
                  <div className="text-slate-400 text-sm mt-1">All approved timesheets are already in a pay run</div>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 w-10"></th>
                      <th className="text-left px-4 py-3">Worker</th>
                      <th className="text-left px-4 py-3">Project</th>
                      <th className="text-left px-4 py-3">Week Start</th>
                      <th className="text-right px-4 py-3">Hours</th>
                      <th className="text-right px-4 py-3">Per Diem</th>
                      <th className="text-left px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {available.map((ts) => (
                      <tr
                        key={ts.id}
                        className={`border-t border-slate-100 cursor-pointer transition-colors ${selectedTs.includes(ts.id) ? "bg-green-50 hover:bg-green-100" : "hover:bg-slate-50"}`}
                        onClick={() => toggleTs(ts.id)}
                        data-testid={`add-ts-row-${ts.id}`}
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" checked={selectedTs.includes(ts.id)} onChange={() => toggleTs(ts.id)}
                            className="w-4 h-4 accent-green-600 cursor-pointer" />
                        </td>
                        <td className="px-4 py-3 font-medium">{ts._worker_name}</td>
                        <td className="px-4 py-3 text-slate-600">{ts._project_name}</td>
                        <td className="px-4 py-3 font-mono text-slate-700">{ts.week_start}</td>
                        <td className="text-right px-4 py-3 font-mono">{ts.total_hours}</td>
                        <td className="text-right px-4 py-3 font-mono">{ts.per_diem_days}</td>
                        <td className="px-4 py-3"><StatusPill status={ts.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="p-5 border-t border-slate-200 flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {selectedTs.length > 0 ? `${selectedTs.length} timesheet${selectedTs.length > 1 ? "s" : ""} selected` : "No timesheets selected"}
              </span>
              <div className="flex gap-2">
                <button onClick={() => setShowAddModal(false)}
                  className="h-10 px-4 border border-slate-300 text-slate-700 font-semibold rounded-md hover:bg-slate-50 transition-colors">
                  Cancel
                </button>
                <button data-testid="add-timesheets-confirm-btn" onClick={addSelected}
                  disabled={selectedTs.length === 0 || adding}
                  className="h-10 px-5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md disabled:opacity-60 transition-colors">
                  {adding ? "Adding…" : `Add ${selectedTs.length > 0 ? `${selectedTs.length} ` : ""}to Pay Run`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
